import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  DashboardState,
  DashboardConfig,
  LayoutItem,
  DataSourceState,
} from '@/lib/dashboard/types';

const initialState: DashboardState = {
  config: null,
  configLoading: false,
  configError: null,
  inlineData: null,
  metadata: null,
  dataSources: {},
  filterValues: {},
  layout: [],
  originalLayout: [],
  layoutDirty: false,
  editMode: false,
  maximizedWidget: null,
  refreshing: false,
  lastRefreshedAt: null,
  breakpoint: 'lg',
  selectedRows: {},
};

export const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    // Config management
    setConfig: (state, action: PayloadAction<DashboardConfig>) => {
      state.config = action.payload;
      state.layout = action.payload.layout;
      state.originalLayout = action.payload.layout;
      state.layoutDirty = false;

      // Initialize filter values with defaults
      if (action.payload.filters) {
        action.payload.filters.forEach((filter) => {
          if (filter.default !== undefined) {
            state.filterValues[filter.id] = filter.default;
          }
        });
      }
    },
    setConfigLoading: (state, action: PayloadAction<boolean>) => {
      state.configLoading = action.payload;
    },
    setConfigError: (state, action: PayloadAction<string | null>) => {
      state.configError = action.payload;
    },
    setInlineData: (state, action: PayloadAction<Record<string, unknown> | null>) => {
      state.inlineData = action.payload;
    },
    setMetadata: (state, action: PayloadAction<import('@/lib/dashboard/types').DashboardMetadata | null>) => {
      state.metadata = action.payload;
    },

    // Data source management
    setDataSourceLoading: (
      state,
      action: PayloadAction<{ sourceId: string }>
    ) => {
      const { sourceId } = action.payload;
      state.dataSources[sourceId] = {
        ...state.dataSources[sourceId],
        loading: true,
        error: null,
      };
    },
    setDataSourceData: (
      state,
      action: PayloadAction<{ sourceId: string; data: unknown }>
    ) => {
      const { sourceId, data } = action.payload;
      state.dataSources[sourceId] = {
        data,
        loading: false,
        error: null,
        lastFetchedAt: new Date().toISOString(),
      };
    },
    setDataSourceError: (
      state,
      action: PayloadAction<{ sourceId: string; error: string }>
    ) => {
      const { sourceId, error } = action.payload;
      state.dataSources[sourceId] = {
        ...state.dataSources[sourceId],
        loading: false,
        error,
      };
    },

    // Filter management
    setFilterValue: (
      state,
      action: PayloadAction<{ filterId: string; value: unknown }>
    ) => {
      state.filterValues[action.payload.filterId] = action.payload.value;
    },
    setMultipleFilterValues: (
      state,
      action: PayloadAction<Record<string, unknown>>
    ) => {
      state.filterValues = { ...state.filterValues, ...action.payload };
    },
    resetFilters: (state) => {
      state.filterValues = {};
      if (state.config?.filters) {
        state.config.filters.forEach((filter) => {
          if (filter.default !== undefined) {
            state.filterValues[filter.id] = filter.default;
          }
        });
      }
    },

    // Layout management
    updateLayout: (state, action: PayloadAction<LayoutItem[]>) => {
      state.layout = action.payload;
      state.layoutDirty = true;
    },
    resetLayout: (state) => {
      state.layout = state.originalLayout;
      state.layoutDirty = false;
    },
    markLayoutSaved: (state) => {
      state.originalLayout = state.layout;
      state.layoutDirty = false;
    },
    removeWidgetFromLayout: (state, action: PayloadAction<string>) => {
      state.layout = state.layout.filter((item) => item.i !== action.payload);
      state.layoutDirty = true;
    },

    // UI state
    toggleEditMode: (state) => {
      state.editMode = !state.editMode;
      // Reset layout if exiting edit mode without saving
      if (!state.editMode && state.layoutDirty) {
        // Keep changes, user can save or reset manually
      }
    },
    setEditMode: (state, action: PayloadAction<boolean>) => {
      state.editMode = action.payload;
    },
    setMaximizedWidget: (state, action: PayloadAction<string | null>) => {
      state.maximizedWidget = action.payload;
    },
    setBreakpoint: (
      state,
      action: PayloadAction<'sm' | 'md' | 'lg' | 'xl'>
    ) => {
      state.breakpoint = action.payload;
    },

    // Refresh management
    setRefreshing: (state, action: PayloadAction<boolean>) => {
      state.refreshing = action.payload;
      if (!action.payload) {
        state.lastRefreshedAt = new Date().toISOString();
      }
    },

    // Row selection
    setSelectedRows: (
      state,
      action: PayloadAction<{ widgetId: string; rowIds: string[] }>
    ) => {
      state.selectedRows[action.payload.widgetId] = action.payload.rowIds;
    },
    clearSelectedRows: (state, action: PayloadAction<string>) => {
      delete state.selectedRows[action.payload];
    },
    clearAllSelectedRows: (state) => {
      state.selectedRows = {};
    },

    // Reset entire dashboard state
    resetDashboard: () => initialState,
  },
});

export const {
  setConfig,
  setConfigLoading,
  setConfigError,
  setInlineData,
  setMetadata,
  setDataSourceLoading,
  setDataSourceData,
  setDataSourceError,
  setFilterValue,
  setMultipleFilterValues,
  resetFilters,
  updateLayout,
  resetLayout,
  markLayoutSaved,
  removeWidgetFromLayout,
  toggleEditMode,
  setEditMode,
  setMaximizedWidget,
  setBreakpoint,
  setRefreshing,
  setSelectedRows,
  clearSelectedRows,
  clearAllSelectedRows,
  resetDashboard,
} = dashboardSlice.actions;
